import { useState } from 'react'
import clsx from 'clsx'
import { AlertOctagon, LayoutGrid, Map as MapIcon, Phone, Truck, Wrench } from 'lucide-react'
import { EmptyState } from '../../cc'
import { BreakdownForm } from '../BreakdownForm'
import { BreakdownQuickResolve } from '../BreakdownQuickResolve'
import {
  detailDangerBtn,
  detailGhostBtn,
  detailHeader,
  detailPrimaryBtn,
  detailShell,
  reportCard,
  reportCardSub,
  reportCardTitle,
} from '../detailUi'
import { DetailSectionCard } from './DetailSectionCard'
import { DetailSectionModal } from './DetailSectionModal'
import { useVehicleDetail } from './useVehicleDetail'
import type { DetailSectionId } from './types'

export function VehicleDetailPage({
  vehicleId,
  onBackToMap,
  onBackToBrowse,
  onReplayTrip,
}: {
  vehicleId: string | null
  onBackToMap: () => void
  /** Return to full-page tab grids without leaving detail mode. */
  onBackToBrowse?: () => void
  /** Replay latest completed leg for this vehicle on the map. */
  onReplayTrip?: () => void
}): JSX.Element {
  const detail = useVehicleDetail(vehicleId)
  const [openSection, setOpenSection] = useState<DetailSectionId | null>(null)
  const [form, setForm] = useState(false)

  const chrome = (
    <>
      {onBackToBrowse && (
        <button type="button" onClick={onBackToBrowse} className={detailGhostBtn}>
          <LayoutGrid size={12} />
          All panels
        </button>
      )}
      <button type="button" onClick={onBackToMap} className={detailPrimaryBtn}>
        <MapIcon size={12} />
        Back to map
      </button>
    </>
  )

  if (!vehicleId || !detail) {
    return (
      <div className={detailShell}>
        <header className={detailHeader}>
          <div className="min-w-0 flex-1">
            <h1 className={reportCardTitle}>Vehicle details</h1>
            <p className={reportCardSub}>Pick a plate from the fleet grid</p>
          </div>
          {chrome}
        </header>
        <div className="grid flex-1 place-items-center p-6">
          <div className={clsx(reportCard, 'max-w-sm')}>
            <EmptyState
              icon={<Truck size={28} />}
              title="Select a vehicle"
              body="Pick a plate from the fleet grid to see its detail cards."
            />
          </div>
        </div>
      </div>
    )
  }

  const { vehicle, down, state, stateColor, where, seenLabel, loadRoute, sections,
    driverPhone, driverName, trip } = detail
  const modalSection = openSection
    ? sections.find((s) => s.id === openSection) ?? null
    : null

  return (
    <div className={detailShell}>
      <header className={clsx(detailHeader, 'items-start')}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <i className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: stateColor }} />
            <h1 className="truncate font-mono text-lg font-semibold tabular-nums text-slate-900">
              {vehicle.vehicle_id}
            </h1>
          </div>
          <p className={clsx(reportCardSub, 'mt-0.5 truncate')}>
            {state} · {where ? where.name : 'in transit'} · seen {seenLabel}
          </p>
          {driverName && (
            <p className="mt-0.5 truncate text-xs font-medium text-slate-700">{driverName}</p>
          )}
          {loadRoute && (
            <p className="mt-0.5 truncate font-mono text-xs tabular-nums text-slate-400">{loadRoute}</p>
          )}
          {(trip?.lr_number || trip?.event || trip?.trailer) && (
            <p className="mt-0.5 truncate font-mono text-xs tabular-nums text-slate-500">
              {trip?.lr_number ? <>LR {trip.lr_number}</> : null}
              {trip?.trailer ? <>{trip.lr_number ? ' · ' : ''}{trip.trailer}</> : null}
              {trip?.event ? <>{(trip.lr_number || trip.trailer) ? ' · ' : ''}{trip.event}</> : null}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <a
            href={driverPhone ? `tel:${driverPhone}` : undefined}
            aria-disabled={!driverPhone}
            className={clsx(detailGhostBtn, !driverPhone && 'pointer-events-none opacity-50')}
            title={driverName ?? 'no driver on the roster'}
          >
            <Phone size={12} />
            {driverName ? driverName.split(' ')[0] : 'No contact'}
          </a>
          <button
            type="button"
            onClick={() => setForm(true)}
            className={detailDangerBtn(down != null)}
          >
            <Wrench size={12} />
            {down ? 'Update breakdown' : 'Mark breakdown'}
          </button>
          {chrome}
        </div>
      </header>

      {down && (
        <div className="shrink-0 border-b border-rose-200 bg-rose-50 px-4 py-3">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-rose-800">
            <AlertOctagon size={14} className="shrink-0" />
            {down.category} · {down.severity}
          </div>
          {down.eta_min != null && (
            <p className="mt-1 text-xs leading-snug text-slate-600">back in ~{down.eta_min} min</p>
          )}
          {/* note (editable) and who/when now live in BreakdownQuickResolve below */}
          <div className="mt-2">
            <BreakdownQuickResolve vehicleId={vehicle.vehicle_id} breakdown={down} />
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {sections.map((section) => (
            <DetailSectionCard
              key={section.id}
              section={section}
              onOpen={setOpenSection}
              onReplay={section.replayable ? onReplayTrip : undefined}
            />
          ))}
        </div>
      </div>

      {modalSection && (
        <DetailSectionModal
          section={modalSection}
          vehicleId={vehicle.vehicle_id}
          onClose={() => setOpenSection(null)}
        />
      )}

      {form && (
        <BreakdownForm
          vehicleId={vehicle.vehicle_id}
          phone={driverPhone}
          onClose={() => setForm(false)}
        />
      )}
    </div>
  )
}
